import React from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { Container, Section, Kicker, Pill } from '../components/ui/Primitives';
import { ARTICLES } from '../data/insights';
import { SERVICE_LINES, SECTORS } from '../data/services';
const PAGE_SIZE = 4;
export function Insights() {
  const [query, setQuery] = React.useState('');
  const [line, setLine] = React.useState<string>('all');
  const [sector, setSector] = React.useState<string>('all');
  const [visible, setVisible] = React.useState(PAGE_SIZE);
  const featured = ARTICLES.find(article => article.featured) ?? ARTICLES[0];
  const filtered = React.useMemo(() => {
    const term = query.trim().toLowerCase();
    return ARTICLES.filter(article => {
      if (line !== 'all' && article.serviceLine !== line) return false;
      if (sector !== 'all' && !article.sectors.includes(sector)) return false;
      if (!term) return true;
      return article.title.toLowerCase().includes(term) || article.excerpt.toLowerCase().includes(term) || article.sectors.some(entry => entry.toLowerCase().includes(term));
    });
  }, [query, line, sector]);
  React.useEffect(() => setVisible(PAGE_SIZE), [query, line, sector]);
  const shown = filtered.slice(0, visible);
  return <>
      <section className="surface-dark" data-id="element-750" data-mp="x218a9k3jhh6">
        <Container className="py-14 sm:py-20" data-id="element-751" data-mp="x0zlx8xmr8k5">
          <Kicker data-id="element-752" data-mp="1b1k2rzjziub0">Insights</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-753" data-mp="1b0r31ijg152d">
            Analysis for people who have to act on it
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-754" data-mp="1ks507jnx9ssc">
            We publish what we would tell a client. Specific, sourced and short enough to read before a meeting.
          </p>
        </Container>
      </section>

      <Section aria-labelledby="featured" data-id="element-755" data-mp="1uqkpdh3e2rz7">
        <h2 id="featured" className="sr-only" data-id="element-756" data-mp="1pr85n57te10u">
          Featured article
        </h2>
        <article className="grid items-center gap-8 rounded-md border border-line p-5 sm:p-8 lg:grid-cols-2" data-id="element-757" data-mp="1pp58m188v7mm">
          <Link to={`/insights/${featured.slug}`} className="block overflow-hidden rounded-sm" data-id="element-758" data-mp="21szqm01p7d0t">
            <img src={featured.heroImage} alt={featured.heroAlt} className="aspect-[3/2] w-full object-cover" data-id="element-759" data-mp="26sc968ljdxxl" />
          </Link>
          <div data-id="element-760" data-mp="2bkvpvefit96m">
            <Kicker data-id="element-761" data-mp="2gifen89uh4ct">Featured</Kicker>
            <h3 className="mt-4 font-display text-[26px] leading-tight text-ink sm:text-[32px]" data-id="element-762" data-mp="2gh41wiyf8m9k">
              <Link to={`/insights/${featured.slug}`} className="underline-offset-4 hover:underline" data-id="element-763" data-mp="2kiplen5gnysq">
                {featured.title}
              </Link>
            </h3>
            <p className="mt-4 text-[16px] text-body" data-id="element-764" data-mp="2uigf4mm769rr">{featured.excerpt}</p>
            <p className="mt-5 text-small text-muted" data-id="element-765" data-mp="2ufkgew2izh29">
              {featured.author.name}, {featured.author.role} · {featured.readTime} minute read
            </p>
          </div>
        </article>
      </Section>

      <Section tone="mist" aria-labelledby="all-insights" data-id="element-766" data-mp="sk4k56rrjd52">
        <h2 id="all-insights" className="font-display text-[28px] text-ink" data-id="element-767" data-mp="slpdwu696s1v">
          All insights
        </h2>

        <div className="mt-8 grid gap-4 rounded-md border border-line bg-white p-5 lg:grid-cols-[1.4fr_1fr_1fr]" data-id="element-768" data-mp="nrl9ahkgmw3y">
          <div data-id="element-769" data-mp="dvi75bplyxh0">
            <label htmlFor="insight-search" className="block text-[13px] font-medium text-ink" data-id="element-770" data-mp="dw0a0r6s09xw">
              Search
            </label>
            <div className="relative mt-2" data-id="element-771" data-mp="1biql8lna548z">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-772" data-mp="16k5tzbq09uwo" />
              <input id="insight-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search titles and sectors" className="w-full rounded-sm border border-line py-2.5 pl-9 pr-3 text-[15px] text-ink" data-id="element-773" data-mp="11mvxjc478izc" />
            </div>
          </div>
          <div data-id="element-774" data-mp="19yxezkkhtbya">
            <label htmlFor="insight-line" className="block text-[13px] font-medium text-ink" data-id="element-775" data-mp="1erqhe1sho877">
              Service line
            </label>
            <select id="insight-line" value={line} onChange={event => setLine(event.target.value)} className="mt-2 w-full rounded-sm border border-line px-3 py-2.5 text-[15px] text-ink" data-id="element-776" data-mp="103vn4gyu1vs0">
              <option value="all" data-id="element-777" data-mp="1xqkw5aryd8nd">All lines</option>
              {SERVICE_LINES.map(item => <option key={item.id} value={item.id} data-id="element-778" data-mp="1itc1a73fxmqw">
                  {item.name}
                </option>)}
            </select>
          </div>
          <div data-id="element-779" data-mp="1nqu2tcf4ozov">
            <label htmlFor="insight-sector" className="block text-[13px] font-medium text-ink" data-id="element-780" data-mp="1nuhvk9cr1i2j">
              Sector
            </label>
            <select id="insight-sector" value={sector} onChange={event => setSector(event.target.value)} className="mt-2 w-full rounded-sm border border-line px-3 py-2.5 text-[15px] text-ink" data-id="element-781" data-mp="cdrm8hd14a4p">
              <option value="all" data-id="element-782" data-mp="2lo0tucsosw1w">All sectors</option>
              {SECTORS.map(item => <option key={item} value={item} data-id="element-783" data-mp="2zjl8ux5aou41">
                  {item}
                </option>)}
            </select>
          </div>
        </div>

        <p aria-live="polite" className="mt-6 text-small text-muted" data-id="element-784" data-mp="27vkrzfbiv42k">
          {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}
        </p>

        {filtered.length === 0 ? <div className="mt-6 rounded-md border border-dashed border-line bg-white p-10 text-center" data-id="element-785" data-mp="2gnv4525z9jmg">
            <p className="font-display text-[20px] text-ink" data-id="element-786" data-mp="2bvbhzgt85g61">Nothing matches those filters</p>
            <p className="mt-3 text-[15px] text-body" data-id="element-787" data-mp="1o5h7h5wdo3oy">
              Try a broader search, or clear the sector filter to see the full library.
            </p>
          </div> : <ul className="mt-6 space-y-6" data-id="element-788" data-mp="3jagtzjfhi4mp">
            {shown.map(article => <li key={article.slug} data-id="element-789" data-mp="3o89zea8yqulx">
                <article className="grid gap-6 rounded-md border border-line bg-white p-5 sm:grid-cols-[240px_1fr] sm:p-6" data-id="element-790" data-mp="3o779o5ndmy7l">
                  <Link to={`/insights/${article.slug}`} className="block overflow-hidden rounded-sm" data-id="element-791" data-mp="3t1l70dllnfew">
                    <img src={article.heroImage} alt={article.heroAlt} loading="lazy" className="aspect-[3/2] w-full object-cover" data-id="element-792" data-mp="1v1p6ehcip73" />
                  </Link>
                  <div data-id="element-793" data-mp="3a87mz3jfcte2">
                    <div className="flex flex-wrap gap-2" data-id="element-794" data-mp="3ab3iymbpp7zv">
                      <Pill data-id="element-795" data-mp="3f95031pkux8p">{SERVICE_LINES.find(item => item.id === article.serviceLine)?.short}</Pill>
                      {article.sectors.map(entry => <Pill key={entry} data-id="element-796" data-mp="uhuw5elbwkpj">{entry}</Pill>)}
                    </div>
                    <h3 className="mt-4 font-display text-[22px] leading-snug text-ink" data-id="element-797" data-mp="2eznh450lpmgo">
                      <Link to={`/insights/${article.slug}`} className="underline-offset-4 hover:underline" data-id="element-798" data-mp="2a0u2g99kvpex">
                        {article.title}
                      </Link>
                    </h3>
                    <p className="mt-3 text-[15px] text-body" data-id="element-799" data-mp="20b0vjlkdryh0">{article.excerpt}</p>
                    <p className="mt-4 text-small text-muted" data-id="element-800" data-mp="2096hmr7j16mf">
                      {new Date(article.publishedAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}{' '}
                      · {article.readTime} minute read
                    </p>
                  </div>
                </article>
              </li>)}
          </ul>}

        {visible < filtered.length && <div className="mt-8 text-center" data-id="element-801" data-mp="3bqfpeghama7w">
            <button type="button" onClick={() => setVisible(current => current + PAGE_SIZE)} className="rounded-sm border border-royal/30 px-6 py-3 text-[15px] text-royal transition-colors hover:bg-white" data-id="element-802" data-mp="3bqpgk7e2qnf6">
              Load more articles
            </button>
          </div>}
      </Section>
    </>;
}